import { ChangeEvent, DragEvent, useRef, useState } from "react";
import {
  Button,
  CircularProgress,
  Container,
  Divider,
  GridLegacy as Grid,
  IconButton,
  Typography,
  alpha,
} from "@mui/material";
import { ArrowBack, ArrowForward, Close, InsertDriveFileOutlined, UploadFile } from "@mui/icons-material";
import clsx from "clsx";
import { useSnackbar } from "notistack";
import { makeStyles } from "@/compat/muiStyles";
import { useHistory, useParams } from "@/compat/router";
import { KeyboardDatePicker } from "@/compat/pickers";
import CardBase from "../../../components/cards/CardBase";
import { formatISODateTime } from "../../../utils/dateTimeUtils";
import useUploadRiskAssessmentVersion from "../../hooks/mutations/useUploadRiskAssessmentVersion";
import moduleAreas, { moduleUrls } from "../../constants/moduleAreaConstants";
import { baseRoute } from "../../routes";

const ACCEPTED_EXTENSIONS = [".pdf", ".docx"];
const MAX_BYTES = 25 * 1024 * 1024;

const useStyles = makeStyles((theme) => ({
  intro: { color: theme.palette.text.secondary, marginBottom: theme.spacing(2) },
  dropzone: {
    border: `2px dashed ${theme.palette.divider}`,
    borderRadius: theme.spacing(1.5),
    padding: theme.spacing(5, 2),
    textAlign: "center",
    cursor: "pointer",
    transition: "border-color .15s, background-color .15s",
    "&:hover": { borderColor: theme.palette.secondary.light },
  },
  dropzoneActive: {
    borderColor: theme.palette.secondary.main,
    backgroundColor: alpha(theme.palette.secondary.main, 0.04),
  },
  dropIcon: {
    width: 56,
    height: 56,
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    margin: "0 auto",
    marginBottom: theme.spacing(1.5),
    backgroundColor: alpha(theme.palette.secondary.main, 0.12),
    color: theme.palette.secondary.main,
  },
  dropHint: {
    color: theme.palette.text.secondary,
    maxWidth: 460,
    margin: `${theme.spacing(1)} auto ${theme.spacing(2)}`,
  },
  hiddenInput: { display: "none" },
  fileRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    padding: theme.spacing(1.5),
    borderRadius: theme.spacing(1),
    backgroundColor: alpha(theme.palette.secondary.main, 0.06),
  },
  fileName: { flex: 1, minWidth: 0, wordBreak: "break-word", fontWeight: 600 },
  fileSize: { color: theme.palette.text.secondary, whiteSpace: "nowrap" },
  block: { marginTop: theme.spacing(3) },
  footer: {
    display: "flex",
    justifyContent: "space-between",
    marginTop: theme.spacing(4),
  },
}));

const formatBytes = (bytes: number) =>
  bytes >= 1024 * 1024 ? `${(bytes / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`;

export default function UploadRiskAssessmentVersion() {
  const classes = useStyles();
  const history = useHistory();
  const { riskAssessmentId } = useParams();
  const { enqueueSnackbar } = useSnackbar();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [file, setFile] = useState<File | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const [dueDate, setDueDate] = useState<Date | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const uploadVersion = useUploadRiskAssessmentVersion();

  const raBase = `${baseRoute}/${moduleUrls[moduleAreas.RISKASSESSMENTS]}`;
  const viewRoute = `${raBase}/view/${riskAssessmentId}`;

  const acceptFile = (candidate: File) => {
    const isValidType = ACCEPTED_EXTENSIONS.some((ext) => candidate.name.toLowerCase().endsWith(ext));
    if (!isValidType) {
      enqueueSnackbar(`${candidate.name}: only PDF or Word documents are accepted.`, { variant: "error" });
      return;
    }
    if (candidate.size > MAX_BYTES) {
      enqueueSnackbar(`${candidate.name}: exceeds the 25 MB limit.`, { variant: "error" });
      return;
    }
    setFile(candidate);
  };

  const onDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragActive(false);
    if (e.dataTransfer?.files?.length) acceptFile(e.dataTransfer.files[0]);
  };

  const onFileInput = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.length) acceptFile(e.target.files[0]);
    e.target.value = "";
  };

  const onContinue = async () => {
    if (!file || isSubmitting) return;
    setIsSubmitting(true);
    try {
      await uploadVersion.mutateAsync({
        riskAssessmentId,
        file,
        dueDate: dueDate ? formatISODateTime(dueDate) : undefined,
      });
      enqueueSnackbar("New version uploaded.", { variant: "success" });
      history.push(viewRoute);
    } catch (err: any) {
      console.error(err);
      const data = err?.response?.data;
      const status = err?.response?.status;
      const apiMessage = typeof data === "string" ? data : data?.message;
      const message =
        status && status < 500 && apiMessage?.trim()
          ? apiMessage
          : "Could not upload the new version — something went wrong on the server. Please try again.";
      enqueueSnackbar(message, { variant: "error" });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Container>
      <CardBase title="Upload New Version" description="Review this risk assessment by uploading an updated document.">
        <Typography variant="body2" className={classes.intro}>
          The previous versions are kept in the history, and the next review date moves on according to the review
          frequency.
        </Typography>

        {!file ? (
          <div
            className={clsx(classes.dropzone, { [classes.dropzoneActive]: dragActive })}
            onDragOver={(e) => {
              e.preventDefault();
              setDragActive(true);
            }}
            onDragLeave={() => setDragActive(false)}
            onDrop={onDrop}
            onClick={() => fileInputRef.current?.click()}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.docx"
              className={classes.hiddenInput}
              onChange={onFileInput}
            />
            <div className={classes.dropIcon}>
              <UploadFile />
            </div>
            <Typography variant="h6">Drag &amp; drop the updated document here</Typography>
            <Typography variant="body2" className={classes.dropHint}>
              PDF and Word files are accepted, maximum 25 MB.
            </Typography>
            <Button
              variant="contained"
              color="primary"
              onClick={(e) => {
                e.stopPropagation();
                fileInputRef.current?.click();
              }}
            >
              Browse files
            </Button>
          </div>
        ) : (
          <>
            <div className={classes.fileRow}>
              <InsertDriveFileOutlined color="secondary" />
              <Typography variant="body2" className={classes.fileName}>
                {file.name}
              </Typography>
              <Typography variant="caption" className={classes.fileSize}>
                {formatBytes(file.size)}
              </Typography>
              <IconButton size="small" aria-label="remove" onClick={() => setFile(null)}>
                <Close fontSize="small" />
              </IconButton>
            </div>
            <div className={classes.block}>
              <Divider />
              <Grid container spacing={2} style={{ marginTop: 8 }}>
                <Grid item xs={12} md={6}>
                  <KeyboardDatePicker
                    format="dd/MM/yyyy"
                    size="small"
                    fullWidth
                    label="Next review date (optional)"
                    inputVariant="outlined"
                    value={dueDate}
                    onChange={(date: Date | null) => setDueDate(date)}
                  />
                </Grid>
              </Grid>
              <Typography variant="caption" color="textSecondary">
                Leave blank to use the review frequency.
              </Typography>
            </div>
          </>
        )}

        <div className={classes.footer}>
          <Button variant="outlined" color="primary" startIcon={<ArrowBack />} onClick={() => history.push(viewRoute)}>
            Back
          </Button>
          <Button
            variant="contained"
            color="primary"
            endIcon={isSubmitting ? <CircularProgress size={18} color="inherit" /> : <ArrowForward />}
            disabled={!file || isSubmitting}
            onClick={onContinue}
          >
            Upload Version
          </Button>
        </div>
      </CardBase>
    </Container>
  );
}
